import qrcode from 'qrcode-generator';
import { useMemo, useRef, useState } from 'react';

/**
 * El editor del panel, en la portada.
 *
 * Que alguien toque un color y vea el código cambiar en el momento vende más
 * que una captura de pantalla del editor, y explica en un segundo que esto no
 * es un generador de códigos negros.
 *
 * Se dibuja con `qrcode-generator` y SVG propio en vez de la librería de
 * estilos que usa el panel: son unos 10 kB frente a unos 50, y este sitio
 * sirve también las páginas que abre quien escanea con datos móviles.
 */

/** Ancho entre alto del archivo del logotipo (966 x 222). */
const PROPORCION_LOGO = 966 / 222;

/** El logotipo, tal cual se sirve. */
const LOGO = '/logo.png';

/** El enlace que iría impreso. Fijo, como en la vida real. */
const ENLACE = 'https://apuntaqr.com/a7f3k2p';

const COLORES = [
  { valor: '#101418', nombre: 'Casi negro' },
  { valor: '#1a3a5c', nombre: 'Azul marino' },
  { valor: '#0f3d2e', nombre: 'Verde bosque' },
  { valor: '#5c1a2e', nombre: 'Granate' },
  { valor: '#3d2b1f', nombre: 'Marrón' },
] as const;

const FORMAS = [
  { id: 'cuadrado', nombre: 'Cuadrados' },
  { id: 'redondo', nombre: 'Redondeados' },
  { id: 'punto', nombre: 'Puntos' },
] as const;

type Forma = (typeof FORMAS)[number]['id'];

/**
 * Dibuja el código.
 *
 * Con logo sube a nivel H de corrección de errores, que recupera hasta un 30%
 * del código dañado. No es un detalle: el logo tapa módulos del centro, y con
 * un nivel menor un código con logo deja de leerse en cuanto la impresión no
 * es perfecta. Es exactamente lo que hace el panel.
 *
 * Los tres cuadrados de las esquinas se dibujan aparte y siempre cuadrados:
 * son los que usa el lector para orientarse, y redondearlos del todo es lo
 * primero que hace que un código falle con poca luz.
 */
function dibujar(forma: Forma, conLogo: boolean) {
  const qr = qrcode(0, conLogo ? 'H' : 'M');
  qr.addData(ENLACE);
  qr.make();
  const n = qr.getModuleCount();

  /*
   * El hueco del logo: una banda horizontal, no un cuadrado.
   *
   * El logotipo de la marca es apaisado —"Apunta QR" en una sola linea— y
   * metido en un cuadrado central se encoge hasta no leerse. Cruzando el
   * codigo cabe entero y grande.
   *
   * El alto sale de la proporcion del propio archivo, no de un numero a ojo:
   * asi se despeja exactamente lo que el logotipo ocupa y ni un modulo mas.
   * Son un 11% del area, y el nivel H recupera hasta el 30%. Ademas la banda
   * no toca ninguno de los tres cuadrados de orientacion, que viven en las
   * esquinas y son los que el lector necesita para encontrar el codigo.
   *
   * Se despejan los modulos de debajo en vez de pintar encima: superponer es
   * lo que rompe la lectura.
   */
  /*
   * El ancho se limita a n-16, y no es un numero arbitrario.
   *
   * A 66% la banda llegaba hasta la columna 6, que es el patron de
   * sincronismo: la fila de modulos alternos que le dice al lector donde cae
   * cada casilla de la rejilla. Eso NO lo cubre la correccion de errores
   * —no son datos, es la regla para leer los datos—, asi que despejarlo es
   * de las pocas cosas que rompen un codigo de verdad. Habria leido casi
   * siempre y fallado justo en la impresion mala, que es el peor modo de
   * fallo posible.
   *
   * Dejando ocho modulos libres a cada lado, la banda nunca alcanza la
   * columna 6 ni por la izquierda ni por la derecha.
   */
  const anchoBanda = conLogo ? Math.min(Math.round(n * 0.66), n - 16) : 0;
  const altoBanda = conLogo ? Math.round(anchoBanda / PROPORCION_LOGO) + 2 : 0;
  const bandaX = Math.floor((n - anchoBanda) / 2);
  const bandaY = Math.floor((n - altoBanda) / 2);

  const enOjo = (x: number, y: number) =>
    (x < 7 && y < 7) || (x > n - 8 && y < 7) || (x < 7 && y > n - 8);
  const enLogo = (x: number, y: number) =>
    conLogo &&
    x >= bandaX &&
    x < bandaX + anchoBanda &&
    y >= bandaY &&
    y < bandaY + altoBanda;

  let modulos = '';
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (!qr.isDark(y, x) || enOjo(x, y) || enLogo(x, y)) continue;
      if (forma === 'punto') {
        modulos += `M${x + 0.5} ${y + 0.08}a0.42 0.42 0 1 1 0 0.84a0.42 0.42 0 1 1 0-0.84z`;
      } else if (forma === 'redondo') {
        modulos += `M${x + 0.12} ${y + 0.5}a0.38 0.38 0 0 1 0.38-0.38h0.2a0.38 0.38 0 0 1 0.38 0.38a0.38 0.38 0 0 1-0.38 0.38h-0.2a0.38 0.38 0 0 1-0.38-0.38z`;
      } else {
        modulos += `M${x} ${y}h1v1h-1z`;
      }
    }
  }

  const ojo = (ox: number, oy: number) =>
    `M${ox} ${oy}h7v7h-7z M${ox + 1} ${oy + 1}v5h5v-5z M${ox + 2} ${oy + 2}h3v3h-3z`;

  return {
    n,
    modulos,
    ojos: `${ojo(0, 0)}${ojo(n - 7, 0)}${ojo(0, n - 7)}`,
    logo: { x: bandaX, y: bandaY, ancho: anchoBanda, alto: altoBanda },
  };
}

export function DemoEnVivo() {
  const [color, setColor] = useState<string>(COLORES[2].valor);
  const [forma, setForma] = useState<Forma>('punto');
  const [conLogo, setConLogo] = useState(true);
  const svg = useRef<SVGSVGElement>(null);

  /**
   * Descarga el código tal y como se ve, en SVG.
   *
   * Se serializa el SVG que ya está en pantalla en vez de volver a dibujarlo:
   * así lo que se baja es exactamente lo que la persona acaba de configurar,
   * sin posibilidad de que las dos versiones se separen.
   *
   * En SVG porque es lo que pide una imprenta y porque escala sin perder
   * nitidez — que es justo lo que va a comprobar quien lo imprima.
   */
  async function descargar() {
    const nodo = svg.current;
    if (!nodo) return;

    const copia = nodo.cloneNode(true) as SVGSVGElement;

    /*
     * El logotipo viaja dentro del archivo.
     *
     * En pantalla basta con apuntar a `/logo.png`, pero un SVG descargado se
     * abre desde el disco de otra persona, donde esa ruta no existe: el
     * codigo saldria con un hueco blanco en mitad. Asi que se incrusta la
     * imagen en el propio archivo antes de guardarlo.
     */
    const imagen = copia.querySelector('image');
    if (imagen) {
      try {
        const datos = await fetch(LOGO).then((r) => r.blob());
        const enTexto = await new Promise<string>((ok, no) => {
          const lector = new FileReader();
          lector.onload = () => ok(String(lector.result));
          lector.onerror = () => no(lector.error);
          lector.readAsDataURL(datos);
        });
        imagen.setAttribute('href', enTexto);
      } catch {
        // Si no se puede traer, mejor un codigo sin logotipo que un codigo
        // con un hueco: el hueco ademas confunde al lector.
        imagen.remove();
      }
    }
    copia.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    copia.setAttribute('width', '1024');
    copia.setAttribute('height', '1024');
    // El fondo blanco va dentro del archivo: sin él, el código impreso sobre
    // papel de color deja de leerse.
    const fondo = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    const caja = copia.getAttribute('viewBox')?.split(' ') ?? ['0', '0', '0', '0'];
    fondo.setAttribute('x', caja[0] ?? '0');
    fondo.setAttribute('y', caja[1] ?? '0');
    fondo.setAttribute('width', caja[2] ?? '0');
    fondo.setAttribute('height', caja[3] ?? '0');
    fondo.setAttribute('fill', '#ffffff');
    copia.insertBefore(fondo, copia.firstChild);

    const texto = new XMLSerializer().serializeToString(copia);
    const url = URL.createObjectURL(new Blob([texto], { type: 'image/svg+xml' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'apuntaqr-prueba.svg';
    a.click();
    URL.revokeObjectURL(url);
  }

  const { n, modulos, ojos, logo } = useMemo(
    () => dibujar(forma, conLogo),
    [forma, conLogo],
  );

  return (
    <div className="demo">
      {/* La prueba de imprenta: papel blanco con marcas de corte, que es como
          le llega un código a una imprenta de verdad. */}
      <div className="demo__prueba">
        <span className="demo__corte demo__corte--ai" aria-hidden="true" />
        <span className="demo__corte demo__corte--ad" aria-hidden="true" />
        <span className="demo__corte demo__corte--bi" aria-hidden="true" />
        <span className="demo__corte demo__corte--bd" aria-hidden="true" />

        <svg
          ref={svg}
          className="demo__qr"
          viewBox={`-1 -1 ${n + 2} ${n + 2}`}
          role="img"
          aria-label="Código QR de ejemplo, que cambia de color y de forma"
        >
          <path d={modulos} fill={color} />
          <path d={ojos} fill={color} fillRule="evenodd" />

          {conLogo ? (
            <g>
              {/*
                El rectangulo blanco no es decorativo. El hueco ya esta
                despejado de modulos, pero sin el, el logotipo quedaria pegado
                a los modulos vecinos; esto le da el aire que necesita para
                leerse, igual que la zona de silencio del codigo entero.
              */}
              <rect
                x={logo.x - 0.6}
                y={logo.y - 0.6}
                width={logo.ancho + 1.2}
                height={logo.alto + 1.2}
                fill="#ffffff"
              />
              {/*
                El logotipo con sus colores, no repintado del color del
                codigo: una marca no cambia de color porque el cartel sea
                granate. Y ahi esta la gracia de la demostracion — quien mire
                esto entiende que el suyo va en ese sitio.
              */}
              <image
                href={LOGO}
                x={logo.x}
                y={logo.y}
                width={logo.ancho}
                height={logo.alto}
                preserveAspectRatio="xMidYMid meet"
              />
            </g>
          ) : null}
        </svg>
      </div>

      <div className="demo__mandos">
        <fieldset className="demo__grupo">
          <legend>Píntalo a tu marca</legend>
          <div className="demo__opciones">
            {COLORES.map((c) => (
              <button
                key={c.valor}
                type="button"
                aria-label={c.nombre}
                aria-pressed={color === c.valor}
                className="demo__color"
                style={{ background: c.valor }}
                onClick={() => setColor(c.valor)}
              />
            ))}
          </div>
        </fieldset>

        <fieldset className="demo__grupo">
          <legend>Forma</legend>
          <div className="demo__opciones">
            {FORMAS.map((f) => (
              <button
                key={f.id}
                type="button"
                aria-pressed={forma === f.id}
                className="demo__opcion"
                onClick={() => setForma(f.id)}
              >
                {f.nombre}
              </button>
            ))}
          </div>
        </fieldset>

        {/* El logo va en su propio grupo y no junto a las formas: elegir forma
            es escoger una de tres, y el logo se pone o no se pone. Mezclarlos
            en la misma fila hacía que se leyera como una cuarta forma. */}
        <fieldset className="demo__grupo">
          <legend>Logo</legend>
          <button
            type="button"
            role="switch"
            aria-checked={conLogo}
            className="demo__interruptor"
            onClick={() => setConLogo(!conLogo)}
          >
            <span className="demo__pista" aria-hidden="true">
              <span className="demo__bolita" />
            </span>
            Tu logo en el centro
          </button>
        </fieldset>

        {/* Que se lo lleve y lo imprima. Es la prueba definitiva de que esto
            funciona, y la hace la persona en su propia impresora. */}
        <button type="button" className="demo__descarga" onClick={() => void descargar()}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M8 2.4v8M4.6 7.2L8 10.6l3.4-3.4M2.8 13.4h10.4"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Descárgalo y haz la prueba
          <span>SVG listo para imprenta</span>
        </button>
      </div>
    </div>
  );
}
